
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient from '../services/apiClient';
import { appConfig } from '../api';
import hotelLogo from '../assets/img/elite-logo.png';
import { getFallbackRoomImage, getRoomImageUrl } from '../utils/roomImageUtils';
import { isVisibleRoomCategory } from '../utils/roomFilters';

const formatCurrency = (value) =>
  `Rs. ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const Rates = () => {
  const [rooms, setRooms] = useState([]);
  const [draftPrices, setDraftPrices] = useState({});
  const [imageVersions, setImageVersions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [toast, setToast] = useState(null);
  const fileInputRefs = useRef({});

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/rooms');
      const fetchedRooms = (response.data || []).filter(isVisibleRoomCategory);
      setRooms(fetchedRooms);

      const nextDraft = {};
      fetchedRooms.forEach((room) => {
        nextDraft[room.id] = String(Number(room.price));
      });
      setDraftPrices(nextDraft);
    } catch (error) {
      console.error('Error fetching room data:', error);
      showToast('Failed to load rooms. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const filteredRooms = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rooms;

    return rooms.filter((room) => {
      const haystack = [
        room.name,
        room.id,
        room.maxPerson,
        room.description,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [rooms, searchQuery]);

  const unsavedCount = useMemo(() => {
    return rooms.filter((room) => {
      const draft = draftPrices[room.id];
      if (draft === undefined) return false;
      return Number(draft) !== Number(room.price);
    }).length;
  }, [rooms, draftPrices]);

  const averageRate = useMemo(() => {
    if (rooms.length === 0) return 0;
    const total = rooms.reduce((sum, room) => {
      const draft = draftPrices[room.id];
      const price = draft !== undefined ? Number(draft) : Number(room.price);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);
    return Math.round(total / rooms.length);
  }, [rooms, draftPrices]);

  const handlePriceChange = (roomId, value) => {
    setDraftPrices((prev) => ({
      ...prev,
      [roomId]: value,
    }));
  };

  const handleDiscard = () => {
    const resetDraft = {};
    rooms.forEach((room) => {
      resetDraft[room.id] = String(Number(room.price));
    });
    setDraftPrices(resetDraft);
    showToast('Unsaved price changes discarded');
  };

  const handleSavePrices = async () => {
    const changes = rooms.filter((room) => {
      const draft = draftPrices[room.id];
      return draft !== undefined && Number(draft) !== Number(room.price);
    });

    if (changes.length === 0) {
      showToast('No price changes to save');
      return;
    }

    for (const room of changes) {
      const parsed = Number(draftPrices[room.id]);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        showToast(`Invalid price for ${room.name} (Room ID ${room.id})`, 'error');
        return;
      }
    }

    setSaving(true);
    try {
      await Promise.all(
        changes.map((room) =>
          apiClient.put(`/rooms/${room.id}/price`, {
            price: Number(draftPrices[room.id]),
          })
        )
      );

      setRooms((prev) =>
        prev.map((room) =>
          changes.some((changed) => changed.id === room.id)
            ? { ...room, price: Number(draftPrices[room.id]) }
            : room
        )
      );
      showToast(`Saved ${changes.length} price change${changes.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error saving prices:', error);
      showToast(error.response?.data?.message || 'Failed to save prices', 'error');
    } finally {
      setSaving(false);
    }
  };

  const triggerImageSelect = (roomId) => {
    fileInputRefs.current[roomId]?.click();
  };

  const handleImageChange = async (roomId, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Please upload a JPEG, PNG, or WEBP image.', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    setUploading((prev) => ({ ...prev, [roomId]: true }));

    try {
      const response = await apiClient.put(`/rooms/${roomId}/image`, formData);
      const updatedImage = response.data?.room?.image;

      if (updatedImage) {
        setRooms((prev) =>
          prev.map((room) =>
            room.id === roomId ? { ...room, image: updatedImage } : room
          )
        );
        setImageVersions((prev) => ({ ...prev, [roomId]: Date.now() }));
      }

      showToast('Room photo updated successfully');
    } catch (error) {
      console.error('Error updating image:', error);
      showToast(error.response?.data?.message || 'Failed to update room photo', 'error');
    } finally {
      setUploading((prev) => ({ ...prev, [roomId]: false }));
    }
  };

  const summaryCards = [
    { label: 'Rooms', value: rooms.length },
    { label: 'Average Rate', value: formatCurrency(averageRate) },
    { label: 'Unsaved Prices', value: unsavedCount },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Reservation Calendar banner */}
      
      {/* Header + search */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-semibold tracking-widest text-red-600 uppercase">Rate Manager</p>
          <h1 className="text-3xl font-bold text-gray-900 font-primary">Manage Room Rates</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Update room prices and replace room photos used by the booking pages.
          </p>
        </div>

        <div className="relative shrink-0 w-full lg:w-80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
          </svg>
          <input
            type="text"
            placeholder="Search room, ID, max guests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-2 pl-9 pr-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="text-4xl font-bold text-gray-900 mt-2">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Save actions */}
      <div className="flex justify-end items-center gap-2 mb-6">
        <button
          type="button"
          onClick={handleDiscard}
          disabled={unsavedCount === 0 || saving}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={handleSavePrices}
          disabled={unsavedCount === 0 || saving}
          className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Prices'}
        </button>
      </div>

      {/* Room grid */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading rooms...</div>
      ) : filteredRooms.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-white border border-gray-200 rounded-xl">
          No rooms match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredRooms.map((room) => {
            const draftPrice = draftPrices[room.id] ?? String(Number(room.price));
            const isDirty = Number(draftPrice) !== Number(room.price);
            const isUploading = uploading[room.id];

            return (
              <div
                key={room.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
              >
                <div className="relative aspect-[4/3] bg-gray-100">
                  <img
                    src={getRoomImageUrl(room, imageVersions[room.id])}
                    alt={room.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = getFallbackRoomImage(room.id);
                    }}
                  />

                  <button
                    type="button"
                    title="Replace room photo"
                    onClick={() => triggerImageSelect(room.id)}
                    disabled={isUploading}
                    className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/95 border border-gray-200 shadow-md flex items-center justify-center hover:bg-white disabled:opacity-60"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M4 20h4l10.5-10.5a2.121 2.121 0 10-3-3L5 17v3z" />
                    </svg>
                  </button>

                  <input
                    ref={(el) => {
                      fileInputRefs.current[room.id] = el;
                    }}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageChange(room.id, file);
                      e.target.value = '';
                    }}
                  />

                  {isUploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-700">Uploading photo...</span>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{room.name}</h2>
                      <p className="text-sm text-gray-500 mt-0.5">Room ID {room.id}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Max {room.maxPerson} guest{room.maxPerson !== 1 ? 's' : ''} · {room.size} SF
                      </p>
                    </div>
                    <span className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                      Current {formatCurrency(room.price)}
                    </span>
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      New Price (Rs.)
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={draftPrice}
                      onChange={(e) => handlePriceChange(room.id, e.target.value)}
                      className={`w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                        isDirty ? 'border-amber-400 bg-amber-50' : 'border-gray-300 bg-white'
                      }`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Rates;
